import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Users, ArrowUpRight, Radio, BrainCircuit, BarChart3, ChevronRight, Search, Loader2, Lock, Shield, Frown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { interests } from '../utils/interests';
import RoomCard from '../components/RoomCard';
import CommunityCard from '../components/CommunityCard';
import { CardSkeleton, RoomCardSkeleton } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';

const ExploreSection = ({ title, icon: Icon, color, children, viewAllPath, loading }) => (
  <section className="mb-16">
    <div className="flex flex-col md:flex-row items-center md:justify-between gap-4 mb-8 text-center md:text-left">
      <div className="flex flex-col md:flex-row items-center gap-3">
        <div className={cn("p-2 rounded-xl shadow-sm", color)}>
          <Icon size={18} className="text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{title}</h2>
        {loading && <Loader2 size={20} className="animate-spin text-gray-400" />}
      </div>
      {viewAllPath && (
        <Link to={viewAllPath} className="text-[10px] font-bold text-gray-400 hover:text-brand-indigo transition-all uppercase tracking-[0.15em] flex items-center gap-1.5 group">
          View All
          <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
      )}
    </div>
    {children}
  </section>
);

const Explore = () => {
  const { communities, rooms } = useActivities();
  const { user } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchResults, setSearchResults] = useState({ communities: [], rooms: [] });
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    setSearchQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    if (!searchQuery && !selectedCategory) {
      setSearchResults({ communities: [], rooms: [] });
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setSearching(true);
      try {
        const query = searchQuery ? `q=${encodeURIComponent(searchQuery)}` : '';
        const cat = selectedCategory ? `category=${encodeURIComponent(selectedCategory)}` : '';
        const params = [query, cat].filter(Boolean).join('&');
        
        const [commRes, roomData] = await Promise.all([
          api.get(`/public/communities?${params}`),
          api.get(`/public/rooms?${params}`)
        ]);
        setSearchResults({ communities: commRes, rooms: roomData });
      } catch (err) {
        console.error('Search failed', err);
      } finally {
        setSearching(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, selectedCategory]);

  const joinedIds = useMemo(() => (Array.isArray(user?.communities) ? user.communities : []).map(c => c.id), [user]);

  const displayCommunities = useMemo(() => {
    if (searchQuery || selectedCategory) return Array.isArray(searchResults.communities) ? searchResults.communities : [];
    
    return [...(Array.isArray(communities) ? communities : [])].sort((a, b) => {
      const aJoined = joinedIds.includes(a.id) ? 1 : 0;
      const bJoined = joinedIds.includes(b.id) ? 1 : 0;
      if (aJoined !== bJoined) return bJoined - aJoined;

      const aLocal = (user?.city && a.city === user.city) || (user?.country && a.country === user.country) ? 1 : 0;
      const bLocal = (user?.city && b.city === user.city) || (user?.country && b.country === user.country) ? 1 : 0;
      if (aLocal !== bLocal) return bLocal - aLocal;

      if (a.is_featured !== b.is_featured) return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      return (b.members_count || 0) - (a.members_count || 0);
    });
  }, [communities, searchQuery, searchResults.communities, user, joinedIds, selectedCategory]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
  };

  const prioritizeRooms = useCallback((roomList) => {
    if (!Array.isArray(roomList)) return [];
    return [...roomList].sort((a, b) => {
      const aJoined = joinedIds.includes(a.community_id) ? 1 : 0;
      const bJoined = joinedIds.includes(b.community_id) ? 1 : 0;
      if (aJoined !== bJoined) return bJoined - aJoined;

      const aLocal = (user?.city && a.city === user.city) || (user?.country && a.country === user.country) ? 1 : 0;
      const bLocal = (user?.city && b.city === user.city) || (user?.country && b.country === user.country) ? 1 : 0;
      if (aLocal !== bLocal) return bLocal - aLocal;

      return (b.participants || 0) - (a.participants || 0);
    });
  }, [joinedIds, user]);

  const mixedRooms = useMemo(() => {
    const allRooms = Array.isArray(rooms) ? rooms : [];
    const searchRooms = Array.isArray(searchResults.rooms) ? searchResults.rooms : [];
    const list = searchQuery ? searchRooms.filter(r => r.type === 'Mixed') : allRooms.filter(r => r.type === 'Mixed');
    return prioritizeRooms(list).slice(0, 3);
  }, [rooms, searchResults.rooms, searchQuery, prioritizeRooms]);

  const qaRooms = useMemo(() => {
    const allRooms = Array.isArray(rooms) ? rooms : [];
    const searchRooms = Array.isArray(searchResults.rooms) ? searchResults.rooms : [];
    const list = searchQuery ? searchRooms.filter(r => r.type === 'Q&A') : allRooms.filter(r => r.type === 'Q&A');
    return prioritizeRooms(list).slice(0, 3);
  }, [rooms, searchResults.rooms, searchQuery, prioritizeRooms]);

  const brainstormRooms = useMemo(() => {
    const allRooms = Array.isArray(rooms) ? rooms : [];
    const searchRooms = Array.isArray(searchResults.rooms) ? searchResults.rooms : [];
    const list = searchQuery ? searchRooms.filter(r => r.type === 'Brainstorm' || r.type === 'Brainstorming') : allRooms.filter(r => r.type === 'Brainstorm' || r.type === 'Brainstorming');
    return prioritizeRooms(list).slice(0, 3);
  }, [rooms, searchResults.rooms, searchQuery, prioritizeRooms]);

  const pollRooms = useMemo(() => {
    const allRooms = Array.isArray(rooms) ? rooms : [];
    const searchRooms = Array.isArray(searchResults.rooms) ? searchResults.rooms : [];
    const list = searchQuery ? searchRooms.filter(r => r.type === 'Poll') : allRooms.filter(r => r.type === 'Poll');
    return prioritizeRooms(list).slice(0, 3);
  }, [rooms, searchResults.rooms, searchQuery, prioritizeRooms]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-12">
      <div className="max-w-3xl mx-auto text-center mb-20">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-8">Discover Chooziq</h1>
        <form onSubmit={handleSearch} className="relative group">
          <div className="absolute inset-0 bg-brand-indigo/5 blur-3xl group-focus-within:bg-brand-indigo/10 transition-all rounded-3xl"></div>
          <div className="relative bg-white border border-gray-100 rounded-3xl p-2 flex flex-col md:flex-row items-stretch md:items-center shadow-2xl shadow-brand-indigo/5 gap-2">
            <div className="hidden md:flex p-3 text-gray-400 group-focus-within:text-brand-indigo transition-colors ml-2">
              {searching ? <Loader2 size={24} className="animate-spin text-brand-indigo" /> : <Search size={24} />}
            </div>
            <div className="flex items-center flex-1 px-6 md:px-0">
              <Search size={20} className="md:hidden text-gray-400 mr-3" />
              <input 
                type="text" 
                placeholder="Search for communities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none font-bold text-base md:text-xl py-4 md:py-2 placeholder:text-gray-300 tracking-tight"
              />
            </div>
            <button 
              type="submit"
              className="bg-brand-indigo text-white px-10 py-4 md:py-5 rounded-2xl font-bold shadow-xl shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all active:scale-[0.98] uppercase tracking-[0.15em] text-[10px]"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      <div className="flex items-center gap-3 overflow-x-auto pb-4 mb-12 no-scrollbar">
        <button
          onClick={() => setSelectedCategory('')}
          className={cn(
            "px-6 py-2.5 rounded-2xl font-bold text-[10px] uppercase tracking-[0.15em] transition-all whitespace-nowrap",
            selectedCategory === '' 
              ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" 
              : "bg-white text-gray-400 border border-gray-100 hover:border-gray-200"
          )}
        >
          All Topics
        </button>
        {interests.map((i) => (
          <button
            key={i.id}
            onClick={() => setSelectedCategory(i.id)}
            className={cn(
              "px-6 py-2.5 rounded-2xl font-bold text-[10px] uppercase tracking-[0.15em] transition-all whitespace-nowrap flex items-center gap-2",
              selectedCategory === i.id 
                ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" 
                : "bg-white text-gray-400 border border-gray-100 hover:border-gray-200"
            )}
          >
            {i.icon}
            {i.label}
          </button>
        ))}
      </div>

      <ExploreSection 
        title={searchQuery || selectedCategory ? "Search Results" : "Active Communities"} 
        icon={Users} 
        color="bg-brand-indigo" 
        viewAllPath="/communities"
        loading={searching}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {searching ? (
            Array(3).fill(0).map((_, i) => <CardSkeleton key={i} />)
          ) : Array.isArray(displayCommunities) && displayCommunities.length > 0 ? (
            displayCommunities.map((c) => (
              <CommunityCard key={c.id} community={c} />
            ))
          ) : (
            <div className="col-span-full">
              <EmptyState 
                title="No communities found" 
                description="Try adjusting your search or filters to find what you're looking for."
                icon={Users}
              />
            </div>
          )}
        </div>
      </ExploreSection>

      <div className="space-y-16">
        <ExploreSection title="Mixed Sessions" icon={Shield} color="bg-brand-accent" viewAllPath="/live">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {searching ? (
              Array(3).fill(0).map((_, i) => <RoomCardSkeleton key={i} />)
            ) : mixedRooms.length > 0 ? (
              mixedRooms.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))
            ) : (
              <div className="col-span-full">
                <EmptyState 
                  title="No mixed sessions" 
                  description="There are no active mixed sessions at the moment. Check back later or create your own."
                  icon={Shield}
                />
              </div>
            )}
          </div>
        </ExploreSection>

        <ExploreSection title="Live Q&A Sessions" icon={Radio} color="bg-purple-500" viewAllPath="/live">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {qaRooms.length > 0 ? (
              qaRooms.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))
            ) : (
              <div className="col-span-full">
                <EmptyState 
                  title="No Q&A sessions" 
                  description="No live Q&A sessions are active right now."
                  icon={Radio}
                  variant="purple"
                />
              </div>
            )}
          </div>
        </ExploreSection>

        <ExploreSection title="Ideation Boards" icon={BrainCircuit} color="bg-green-600" viewAllPath="/live">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {brainstormRooms.length > 0 ? (
              brainstormRooms.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))
            ) : (
              <div className="col-span-full">
                <EmptyState 
                  title="No ideation boards" 
                  description="Brainstorming hubs will appear here when active."
                  icon={BrainCircuit}
                  variant="green"
                />
              </div>
            )}
          </div>
        </ExploreSection>

        <ExploreSection title="Trending Polls" icon={BarChart3} color="bg-brand-indigo" viewAllPath="/live">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pollRooms.length > 0 ? (
              pollRooms.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))
            ) : (
              <div className="col-span-full">
                <EmptyState 
                  title="No active polls" 
                  description="Participate in community decisions when polls are live."
                  icon={BarChart3}
                />
              </div>
            )}
          </div>
        </ExploreSection>
      </div>
    </div>
  );
};

export default Explore;
